import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  GraduationCap, Plus, Users, FileText, Upload, Trophy, 
  BarChart3, Download, Trash2, ChevronRight, Loader2,
  CheckCircle2, XCircle, Clock, Sparkles, ArrowUpDown,
  BookOpen, Building, Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Tier badge colors
const tierColors = {
  starter: 'bg-green-100 text-green-800 border-green-200',
  standard: 'bg-blue-100 text-blue-800 border-blue-200',
  enterprise: 'bg-purple-100 text-purple-800 border-purple-200'
};

const tierLabels = {
  starter: 'Free (Beta)',
  standard: 'Premium',
  enterprise: 'Enterprise'
};

export default function EducatorDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  // State
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [activeTab, setActiveTab] = useState('cohorts');
  
  // Create cohort form
  const [newCohort, setNewCohort] = useState({
    name: '',
    description: '',
    subject: '',
    semester: '',
    institution: '',
    tier: 'starter'
  });
  const [creating, setCreating] = useState(false);
  
  // Load cohorts
  const loadCohorts = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/educator/cohorts`, {
        withCredentials: true
      });
      if (response.data.success) {
        setCohorts(response.data.cohorts);
      }
    } catch (error) {
      console.error('Error loading cohorts:', error);
      if (error.response?.status === 401) {
        toast({
          title: "Authentication Required",
          description: "Please log in to access the Educator Dashboard",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);
  
  useEffect(() => {
    if (isAuthenticated) {
      loadCohorts();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, loadCohorts]);
  
  // Create cohort
  const handleCreateCohort = async () => {
    if (!newCohort.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for your cohort",
        variant: "destructive"
      });
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts`,
        newCohort,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast({
          title: "Cohort Created!",
          description: `"${newCohort.name}" is ready for students`
        });
        setCohorts([response.data.cohort, ...cohorts]);
        setShowCreateModal(false);
        setNewCohort({
          name: '', description: '', subject: '', 
          semester: '', institution: '', tier: 'starter'
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create cohort",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };
  
  // Delete cohort
  const handleDeleteCohort = async (cohortId, cohortName) => {
    if (!window.confirm(`Delete "${cohortName}" and all associated data? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}`,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast({
          title: "Cohort Deleted",
          description: `"${cohortName}" has been removed`
        });
        setCohorts(cohorts.filter(c => c.id !== cohortId));
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete cohort",
        variant: "destructive"
      });
    }
  };
  
  // Unauthenticated view
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="border-2 border-purple-200">
            <CardContent className="pt-12 pb-8 text-center">
              <GraduationCap className="w-16 h-16 mx-auto text-purple-600 mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Educator Dashboard</h2>
              <p className="text-slate-600 mb-6">
                Batch evaluate student papers with OI scoring and leaderboards
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Sign In to Continue
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Educator Dashboard</h1>
              <p className="text-slate-600">Batch evaluate papers & track student progress</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Cohort
          </Button>
        </div>
        
        {/* Pricing Tiers Banner */}
        <Card className="mb-6 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Sparkles className="w-6 h-6 text-purple-600" />
                <div>
                  <p className="font-semibold text-purple-900">Batch Evaluation Tiers</p>
                  <p className="text-sm text-purple-700">
                    <span className="font-medium">Starter:</span> 30 papers • 
                    <span className="font-medium ml-2">Standard:</span> 100 papers • 
                    <span className="font-medium ml-2">Enterprise:</span> 500+ papers
                  </p>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-800 border-green-200">
                Beta: Starter Free
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Cohorts Grid */}
        {cohorts.length === 0 ? (
          <Card className="border-2 border-dashed border-purple-300">
            <CardContent className="py-16 text-center">
              <Users className="w-16 h-16 mx-auto text-purple-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Cohorts Yet</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Create your first cohort to start batch evaluating student papers with OI scores and rankings.
              </p>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Cohort
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cohorts.map(cohort => (
              <Card 
                key={cohort.id} 
                className="border-2 border-slate-200 hover:border-purple-300 transition-all cursor-pointer group"
                onClick={() => navigate(`/educator/cohort/${cohort.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg group-hover:text-purple-700 transition-colors">
                        {cohort.name}
                      </CardTitle>
                      {cohort.subject && (
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <BookOpen className="w-3 h-3" />
                          {cohort.subject}
                          {cohort.semester && ` • ${cohort.semester}`}
                        </CardDescription>
                      )}
                    </div>
                    <Badge className={tierColors[cohort.tier]}>
                      {tierLabels[cohort.tier]}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <Users className="w-4 h-4 mx-auto text-slate-500 mb-1" />
                      <p className="text-lg font-semibold text-slate-900">{cohort.student_count}</p>
                      <p className="text-xs text-slate-500">Students</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <FileText className="w-4 h-4 mx-auto text-slate-500 mb-1" />
                      <p className="text-lg font-semibold text-slate-900">{cohort.evaluation_count}</p>
                      <p className="text-xs text-slate-500">Evaluated</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <Trophy className="w-4 h-4 mx-auto text-amber-500 mb-1" />
                      <p className="text-lg font-semibold text-slate-900">{cohort.max_papers}</p>
                      <p className="text-xs text-slate-500">Max Papers</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      Created {new Date(cohort.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCohort(cohort.id, cohort.name);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Create Cohort Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-6 h-6 text-purple-600" />
                  Create New Cohort
                </CardTitle>
                <CardDescription>
                  Set up a class or batch for evaluating student papers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Cohort Name *
                  </label>
                  <Input
                    placeholder="e.g., CS101 - Fall 2024"
                    value={newCohort.name}
                    onChange={(e) => setNewCohort({...newCohort, name: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Subject
                    </label>
                    <Input
                      placeholder="e.g., Computer Science"
                      value={newCohort.subject}
                      onChange={(e) => setNewCohort({...newCohort, subject: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Semester
                    </label>
                    <Input
                      placeholder="e.g., Fall 2024"
                      value={newCohort.semester}
                      onChange={(e) => setNewCohort({...newCohort, semester: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Institution
                  </label>
                  <Input
                    placeholder="e.g., Stanford University"
                    value={newCohort.institution}
                    onChange={(e) => setNewCohort({...newCohort, institution: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Description
                  </label>
                  <Input
                    placeholder="Brief description of this cohort"
                    value={newCohort.description}
                    onChange={(e) => setNewCohort({...newCohort, description: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Evaluation Tier
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['starter', 'standard', 'enterprise'].map(tier => (
                      <button
                        key={tier}
                        onClick={() => setNewCohort({...newCohort, tier})}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          newCohort.tier === tier
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-slate-200 hover:border-purple-300'
                        }`}
                      >
                        <p className="font-semibold text-slate-900 capitalize">{tier}</p>
                        <p className="text-xs text-slate-600">
                          {tier === 'starter' && 'Up to 30'}
                          {tier === 'standard' && 'Up to 100'}
                          {tier === 'enterprise' && '500+'}
                        </p>
                        <Badge className={`mt-1 ${tierColors[tier]}`}>
                          {tierLabels[tier]}
                        </Badge>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={handleCreateCohort}
                    disabled={creating}
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Cohort
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
